// src/components/recipes/RecipeForm.js
import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
} from "react-bootstrap";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import DynamicBreadcrumb from "../header/breadcrumb";
import recipesAPI from "../../services/recipesAPI";
import Loader from "../loader/loader";
import { showConfirm } from "react-confirm-prompt";
import productAPI from "../../services/productapi";

library.add(fas);

// ---------- helpers ----------
const emptyMediaRow = () => ({
    id: Date.now(),
    fileUrl: "",        // existing URL from server
    altText: "",
    isPrimary: 0,
    file: null,         // File object for new upload
    previewUrl: "",     // local preview URL
});

const emptyIngredientGroup = () => ({
    id: Date.now(),
    title: "",
    collapsed: false,
    ingredients: [
        {
            id: Date.now() + 1,
            ingredientName: "",
            quantityText: "",
        },
    ],
});

const emptyStepSection = () => ({
    id: Date.now(),
    title: "",
    collapsed: false,   // for per-section toggle
    steps: [
        {
            id: Date.now() + 1,
            description: "",
        },
    ],
});

const RecipeForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const recipeId = searchParams.get("recipeId");
    const isEditMode = !!recipeId;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // basic fields
    const [title, setTitle] = useState("");
    const [shortTitle, setShortTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [description, setDescription] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [isActive, setIsActive] = useState(1);

    // media / ingredients / steps
    const [mediaList, setMediaList] = useState([emptyMediaRow()]);
    const [ingredientGroups, setIngredientGroups] = useState([
        emptyIngredientGroup(),
    ]);
    const [stepSections, setStepSections] = useState([emptyStepSection()]);

    // mapped products list
    const [mappedProducts, setMappedProducts] = useState([]);
    // search controls for adding one product at a time
    const [productSearchText, setProductSearchText] = useState("");
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [selectedProductItem, setSelectedProductItem] = useState(null);
    const [productQtyText, setProductQtyText] = useState("1");

    // section collapse flags
    const [mediaOpen, setMediaOpen] = useState(true);
    const [ingredientsOpen, setIngredientsOpen] = useState(true);
    const [productsOpen, setProductsOpen] = useState(true);
    const [stepsOpen, setStepsOpen] = useState(true);

    const [errors, setErrors] = useState({});

    const breadcrumb = [
        { name: "Recipes List", url: "/admin/recipes" },
        { name: isEditMode ? "Edit Recipe" : "Add New Recipe", url: null },
    ];
    const [foodType, setFoodType] = useState(1);

    // ----------------- Load existing recipe in edit mode -----------------
    useEffect(() => {
        const loadRecipe = async () => {
            if (!isEditMode) return;
            setLoading(true);
            try {
                const resp = await recipesAPI.getRecipeDetail(recipeId);

                const payload = resp.data || resp;
                const r = payload.recipe || payload;

                setTitle(r.title || "");
                setShortTitle(r.shortTitle || "");
                setSlug(r.slug || "");
                setDescription(r.description || "");
                setVideoUrl(r.videoUrl || "");
                setFoodType(r.foodType ?? r.food_type ?? 1);
                setIsActive(r.isActive ?? 1);

                setMediaList(
                    (r.media && r.media.length ? r.media : [emptyMediaRow()]).map(
                        (m) => ({
                            id: m.id || Date.now() + Math.random(),
                            fileUrl: m.fileUrl || "",
                            altText: m.altText || "",
                            isPrimary: m.isPrimary || 0,
                            file: null,
                            previewUrl: m.fileUrl || "",
                        })
                    )
                );

                setIngredientGroups(
                    (r.ingredientGroups && r.ingredientGroups.length
                        ? r.ingredientGroups
                        : [emptyIngredientGroup()]
                    ).map((g) => ({
                        id: g.id || Date.now() + Math.random(),
                        title: g.title || "",
                        collapsed: false,
                        ingredients: (g.ingredients || []).length
                            ? g.ingredients.map((ing) => ({
                                id: ing.id || Date.now() + Math.random(),
                                ingredientName: ing.ingredientName || "",
                                quantityText: ing.quantityText || "",
                            }))
                            : [
                                {
                                    id: Date.now() + Math.random(),
                                    ingredientName: "",
                                    quantityText: "",
                                },
                            ],
                    }))
                );

                setStepSections(
                    (r.stepSections && r.stepSections.length
                        ? r.stepSections
                        : [emptyStepSection()]
                    ).map((s) => {
                        const rawSteps = s.steps || s.lines || [];
                        return {
                            id: s.id || Date.now() + Math.random(),
                            title: s.title || "",
                            collapsed: false,
                            steps: rawSteps.length
                                ? rawSteps.map((st) => ({
                                    id: st.id || Date.now() + Math.random(),
                                    description: st.description || "",
                                }))
                                : [
                                    {
                                        id: Date.now() + Math.random(),
                                        description: "",
                                    },
                                ],
                        };
                    })
                );

                // ----- mapped products from server -----
                const apiProducts =
                    r.productItems ||
                    r.recipeProducts ||
                    r.mappedProducts ||
                    [];

                if (apiProducts.length) {
                    setMappedProducts(
                        apiProducts.map((p, idx) => {
                            const item = p.productItem || p; // depending on how ORM returns it

                            const unitText = [
                                item?.item_spec ?? item?.unit,
                                item?.item_unit ?? item?.unitName,
                            ].filter(Boolean).join(" ");

                            const baseLabel =
                                p.label ||
                                item?.itemName ||
                                item?.productName ||
                                item?.product?.productName ||  // from ProductItem -> products
                                item?.name ||
                                "";

                            const code =
                                p.code ||
                                item?.refCode ||
                                item?.item_ref_code ||
                                item?.itemCode ||
                                item?.code ||
                                "";

                            const qtyFromApi =
                                p.quantityText ||
                                p.defaultQty ||
                                p.default_quantity ||
                                p.defaultqty ||
                                "";

                            return {
                                id: p.id || Date.now() + Math.random(),
                                productItemId:
                                    p.productItemId ||
                                    p.product_item_id ||
                                    p.itemId ||
                                    null,
                                label: unitText ? `${baseLabel} - ${unitText}` : baseLabel,
                                code,
                                quantityText: qtyFromApi,
                                sortOrder: p.sortOrder || idx + 1,
                            };
                        })
                    );
                } else {
                    setMappedProducts([]);
                }


            } catch (e) {
                console.error("Failed to load recipe", e);
            } finally {
                setLoading(false);
            }
        };

        loadRecipe();
    }, [isEditMode, recipeId]);

    useEffect(() => {
        return () => {
            mediaList.forEach((m) => {
                if (m.previewUrl && m.file) {
                    URL.revokeObjectURL(m.previewUrl);
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // ----------------- Auto slug if empty -----------------
    const handleTitleBlur = () => {
        if (!slug && title) {
            setSlug(
                title
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "")
            );
        }
    };

    // ----------------- Media handlers -----------------
    const addMediaRow = () => {
        setMediaList([...mediaList, emptyMediaRow()]);
    };

    const removeMediaRow = (id) => {
        if (mediaList.length === 1) return;

        const toRemove = mediaList.find((m) => m.id === id);
        if (toRemove?.previewUrl && toRemove?.file) {
            URL.revokeObjectURL(toRemove.previewUrl);
        }

        setMediaList(mediaList.filter((m) => m.id !== id));
    };

    const updateMediaField = (id, field, value) => {
        setMediaList(
            mediaList.map((m) =>
                m.id === id
                    ? {
                        ...m,
                        [field]:
                            field === "isPrimary" ? (value ? 1 : 0) : value,
                    }
                    : m
            )
        );
    };

    const setPrimaryMedia = (id) => {
        setMediaList(
            mediaList.map((m) => ({
                ...m,
                isPrimary: m.id === id ? 1 : 0,
            }))
        );
    };

    const handleMediaFileChange = (id, file) => {
        if (!file) return;

        setMediaList((prev) =>
            prev.map((m) => {
                if (m.id !== id) return m;

                // revoke previous object url (only if it was created locally, not server url)
                if (m.previewUrl && m.file) {
                    URL.revokeObjectURL(m.previewUrl);
                }

                const preview = URL.createObjectURL(file);
                return { ...m, file, previewUrl: preview };
            })
        );
    };


    // ----------------- Ingredient handlers -----------------
    const toggleIngredientGroupCollapsed = (groupId) => {
        setIngredientGroups(
            ingredientGroups.map((g) =>
                g.id === groupId ? { ...g, collapsed: !g.collapsed } : g
            )
        );
    };

    const addIngredientGroup = () => {
        setIngredientGroups([...ingredientGroups, emptyIngredientGroup()]);
    };

    const removeIngredientGroup = (groupId) => {
        if (ingredientGroups.length === 1) return;
        setIngredientGroups(ingredientGroups.filter((g) => g.id !== groupId));
    };

    const updateGroupTitle = (groupId, value) => {
        setIngredientGroups(
            ingredientGroups.map((g) =>
                g.id === groupId ? { ...g, title: value } : g
            )
        );
    };

    const addIngredientRow = (groupId) => {
        setIngredientGroups(
            ingredientGroups.map((g) =>
                g.id === groupId
                    ? {
                        ...g,
                        ingredients: [
                            ...g.ingredients,
                            {
                                id: Date.now() + Math.random(),
                                ingredientName: "",
                                quantityText: "",
                            },
                        ],
                    }
                    : g
            )
        );
    };

    const removeIngredientRow = (groupId, ingId) => {
        setIngredientGroups(
            ingredientGroups.map((g) => {
                if (g.id !== groupId) return g;
                if (g.ingredients.length === 1) return g;
                return {
                    ...g,
                    ingredients: g.ingredients.filter(
                        (ing) => ing.id !== ingId
                    ),
                };
            })
        );
    };

    const updateIngredientField = (groupId, ingId, field, value) => {
        setIngredientGroups(
            ingredientGroups.map((g) => {
                if (g.id !== groupId) return g;
                return {
                    ...g,
                    ingredients: g.ingredients.map((ing) =>
                        ing.id === ingId ? { ...ing, [field]: value } : ing
                    ),
                };
            })
        );
    };

    // ----------------- Mapped product handlers -----------------

    const runProductSearch = async (text) => {
        if (!text || text.length < 2) {
            setProductSearchResults([]);
            return;
        }

        try {
            const res = await productAPI.getAllProductItems({
                limit: 10,
                page: 0,
                filterModel: { quickFilterValues: [text] },
            });

            const items =
                (res.data && res.data.result) ||
                res.items ||
                res.result ||
                [];

            const mapped = items.map((it) => {
                const unitSpec = it.item_spec ?? it.unit;
                const unitName = it.item_unit ?? it.unitName;
                const unitText = [unitSpec, unitName].filter(Boolean).join(" ");

                const baseLabel =
                    it.itemName ||
                    it.productName ||
                    it.name ||
                    (it.product && (it.product.productName || it.product.pro_name)) ||
                    "";

                const code =
                    it.refCode ||
                    it.item_ref_code ||
                    it.itemCode ||
                    it.item_id ||
                    it.code ||
                    "";

                return {
                    id: it.id,
                    label: unitText ? `${baseLabel} - ${unitText}` : baseLabel,
                    code,
                    item_spec: unitSpec,
                    item_unit: unitName,
                    uomText: unitText,
                };
            });


            setProductSearchResults(mapped);
        } catch (err) {
            console.error("Product search failed", err);
            setProductSearchResults([]);
        }
    };

    const handleProductSearchChange = async (e) => {
        const text = e.target.value;
        setProductSearchText(text);
        setSelectedProductItem(null);
        await runProductSearch(text);
    };

    const handleSelectProductSuggestion = (item) => {
        setSelectedProductItem({
            productItemId: item.id,
            label: item.label,
            code: item.code,
            uomText: item.uomText,
        });
        const baseText = item.code
            ? `${item.label} (${item.code})`
            : item.label;

        setProductSearchText(
            item.uomText ? `${baseText} [${item.uomText}]` : baseText
        );

        setProductSearchResults([]);
        if (!productQtyText.trim()) setProductQtyText("1");
    };

    const handleAddMappedProduct = () => {
        if (!selectedProductItem) {
            return;
        }

        const exists = mappedProducts.some(
            (p) => p.productItemId === selectedProductItem.productItemId
        );
        if (exists) {
            // optional: show alert later if you want
            return;
        }

        const qty = productQtyText.trim() || "1";

        const newRow = {
            id: Date.now(),
            productItemId: selectedProductItem.productItemId,
            label: selectedProductItem.label,
            code: selectedProductItem.code,
            quantityText: qty,
        };

        setMappedProducts([...mappedProducts, newRow]);

        // reset controls
        setSelectedProductItem(null);
        setProductSearchText("");
        setProductQtyText("1");
        setProductSearchResults([]);
    };

    const handleRemoveMappedProduct = (id) => {
        setMappedProducts(mappedProducts.filter((p) => p.id !== id));
    };

    const handleChangeMappedQuantity = (id, value) => {
        setMappedProducts(
            mappedProducts.map((p) =>
                p.id === id ? { ...p, quantityText: value } : p
            )
        );
    };

    const moveMappedProduct = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= mappedProducts.length) return;
        const newList = [...mappedProducts];
        const temp = newList[index];
        newList[index] = newList[newIndex];
        newList[newIndex] = temp;
        setMappedProducts(newList);
    };

    // ----------------- Step handlers -----------------
    const addStepSection = () => {
        setStepSections([...stepSections, emptyStepSection()]);
    };

    const removeStepSection = (sectionId) => {
        if (stepSections.length === 1) return;
        setStepSections(stepSections.filter((s) => s.id !== sectionId));
    };

    const updateSectionTitle = (sectionId, value) => {
        setStepSections(
            stepSections.map((s) =>
                s.id === sectionId ? { ...s, title: value } : s
            )
        );
    };

    const toggleStepSectionCollapsed = (sectionId) => {
        setStepSections(
            stepSections.map((s) =>
                s.id === sectionId ? { ...s, collapsed: !s.collapsed } : s
            )
        );
    };

    const addStepLine = (sectionId) => {
        setStepSections(
            stepSections.map((s) =>
                s.id === sectionId
                    ? {
                        ...s,
                        steps: [
                            ...s.steps,
                            {
                                id: Date.now() + Math.random(),
                                description: "",
                            },
                        ],
                    }
                    : s
            )
        );
    };

    const removeStepLine = (sectionId, stepId) => {
        setStepSections(
            stepSections.map((s) => {
                if (s.id !== sectionId) return s;
                if (s.steps.length === 1) return s;
                return {
                    ...s,
                    steps: s.steps.filter((st) => st.id !== stepId),
                };
            })
        );
    };

    const updateStepDescription = (sectionId, stepId, value) => {
        setStepSections(
            stepSections.map((s) => {
                if (s.id !== sectionId) return s;
                return {
                    ...s,
                    steps: s.steps.map((st) =>
                        st.id === stepId ? { ...st, description: value } : st
                    ),
                };
            })
        );
    };

    // ----------------- Validation & Save -----------------
    const validateForm = () => {
        const errs = {};
        if (!title.trim()) errs.title = "Title is required";
        if (!slug.trim()) errs.slug = "Slug is required";

        const hasIngredient =
            ingredientGroups.some((g) =>
                g.ingredients.some((ing) => ing.ingredientName.trim())
            ) || false;
        if (!hasIngredient) {
            errs.ingredients = "At least one ingredient name is required";
        }

        const hasStep =
            stepSections.some((s) =>
                s.steps.some((st) => st.description.trim())
            ) || false;
        if (!hasStep) {
            errs.steps = "At least one step is required";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const buildPayload = () => {
        return {
            recipeId: isEditMode ? Number(recipeId) : null,
            title: title.trim(),
            shortTitle: shortTitle.trim() || null,
            slug: slug.trim(),
            description: description.trim() || null,
            videoUrl: videoUrl.trim() || null,
            foodType: Number(foodType),
            isActive: Number(isActive),

            media: mediaList
                .filter((m) => (m.fileUrl || "").trim() || m.file)
                .map((m, idx) => ({
                    id: m.id > 0 ? m.id : undefined,
                    fileUrl: m.fileUrl.trim() || null, // for existing ones
                    altText: m.altText.trim() || null,
                    isPrimary: m.isPrimary ? 1 : 0,
                    sortOrder: idx + 1,
                    hasFile: !!m.file, // helper hint for backend
                })),

            ingredientGroups: ingredientGroups.map((g, gIdx) => ({
                id: g.id > 0 ? g.id : undefined,
                title: g.title.trim() || `Group ${gIdx + 1}`,
                sortOrder: gIdx + 1,
                ingredients: g.ingredients
                    .filter((ing) => ing.ingredientName.trim())
                    .map((ing, iIdx) => ({
                        id: ing.id > 0 ? ing.id : undefined,
                        ingredientName: ing.ingredientName.trim(),
                        quantityText: ing.quantityText.trim() || null,
                        sortOrder: iIdx + 1,
                    })),
            })),

            stepSections: stepSections.map((s, sIdx) => ({
                id: s.id > 0 ? s.id : undefined,
                title: s.title.trim() || `Step Section ${sIdx + 1}`,
                sortOrder: sIdx + 1,
                steps: s.steps
                    .filter((st) => st.description.trim())
                    .map((st, iIdx) => ({
                        id: st.id > 0 ? st.id : undefined,
                        description: st.description.trim(),
                        sortOrder: iIdx + 1,
                    })),
            })),

            // mapped product items – sorted by current order
            productItems: mappedProducts
                .filter((p) => p.productItemId)
                .map((p, idx) => ({
                    productItemId: p.productItemId,
                    quantityText: (p.quantityText || "").trim() || null,
                    sortOrder: idx + 1,
                })),
        };
    };

    const handleSubmit = async (e) => {
        // console.log('Submitted');
        e.preventDefault();
        if (!validateForm()) return;
        // console.log('Submitted after prevent');

        const payload = buildPayload();
        const formData = new FormData();
        formData.append("payload", JSON.stringify(payload));

        mediaList.forEach((m, idx) => {
            if (m.file) {
                formData.append(`mediaFiles[${idx}]`, m.file);
            }
        });

        try {
            setSaving(true);

            // 👇 pass recipeId when editing
            const resp = await recipesAPI.saveRecipe(
                formData,
                isEditMode ? Number(recipeId) : null
            );

            const message =
                resp.message ||
                (isEditMode
                    ? "Recipe updated successfully"
                    : "Recipe created successfully");

            showConfirm("Success", {
                description: message,
                type: "Success",
                hideCancel: true,
            }).then(() => {
                navigate("/admin/recipes");
            });
        } catch (err) {
            console.error("Error saving recipe", err);
            showConfirm("Oops!", {
                description: "Save failed, kindly try again later.",
                type: "warning",
                hideCancel: true,
            });
        } finally {
            setSaving(false);
        }
    };


    const handleCancel = () => {
        navigate("/admin/recipes");
    };

    // ----------------- Render -----------------
    if (loading) {
        return (
            <Container className="mt-4 mb-4">
                <Loader />
            </Container>
        );
    }

    return (
        <Container className="mt-4 mb-4">
            <Box className="recipe-form-header d-flex align-items-center justify-content-between mb-3">
                <DynamicBreadcrumb pathArray={breadcrumb} classesName="col-auto" />

                <div className="d-flex gap-2 align-items-center">
                    <Button
                        variant="danger"
                        size="lg"
                        className="recipe-action-btn text-nowrap"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="primary"
                        size="lg"
                        className="recipe-action-btn text-nowrap"
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon="fa-solid fa-floppy-disk" className="me-2" />
                        {saving
                            ? isEditMode ? "Saving..." : "Creating..."
                            : isEditMode ? "Save Changes" : "Create Recipe"}
                    </Button>
                </div>
            </Box>


            {/* Basic Info */}
            <Card className="mb-3">
                <Card.Header className="recipe-section-header">
                    Basic Information
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Recipe Name *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    onBlur={handleTitleBlur}
                                    isInvalid={!!errors.title}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.title}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Short Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={shortTitle}
                                    onChange={(e) => setShortTitle(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Slug *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                    isInvalid={!!errors.slug}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.slug}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Food Type</Form.Label>
                                <Form.Select
                                    value={foodType}
                                    onChange={(e) => setFoodType(Number(e.target.value))}
                                >
                                    <option value={1}>Veg</option>
                                    <option value={2}>Non-Veg</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={isActive}
                                    onChange={(e) => setIsActive(Number(e.target.value))}
                                >
                                    <option value={1}>Active</option>
                                    <option value={2}>Inactive</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Video URL</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="YouTube / MP4 URL"
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Form.Group>
                </Card.Body>
            </Card>

            {/* Media */}
            <Card className="mb-3">
                <Card.Header
                    className="recipe-section-header d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setMediaOpen(!mediaOpen)}
                >
                    <span>Media (Images / Thumbnails)</span>
                    <div className="d-flex align-items-center">
                        <Button
                            size="sm"
                            style={{
                                border: "1px solid #ff8080ff"
                            }}
                            variant="danger"
                            className="me-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                addMediaRow();
                            }}
                        >
                            + Add Image
                        </Button>
                        <FontAwesomeIcon
                            icon={
                                mediaOpen
                                    ? "fa-solid fa-chevron-up"
                                    : "fa-solid fa-chevron-down"
                            }
                        />
                    </div>
                </Card.Header>
                {mediaOpen && (
                    <Card.Body>
                        {mediaList.map((m, idx) => {
                            const serverSrc = m.fileUrl
                                ? `${process.env.REACT_APP_IMAGE_URL}/uploads/${m.fileUrl}`
                                : "";

                            const displaySrc = m.previewUrl || serverSrc;

                            return (
                                <Row key={m.id} className="align-items-center mb-3">
                                    <Col md={4}>
                                        <Form.Label>Preview #{idx + 1}</Form.Label>

                                        <div className="mb-2">
                                            {displaySrc ? (
                                                <img
                                                    src={displaySrc}
                                                    alt={m.altText || `Image ${idx + 1}`}
                                                    style={{
                                                        maxWidth: "100%",
                                                        maxHeight: "120px",
                                                        borderRadius: "6px",
                                                        border: "1px solid #e5e5e5",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        height: 120,
                                                        borderRadius: 6,
                                                        border: "1px dashed #ccc",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#888",
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    No image selected
                                                </div>
                                            )}
                                        </div>

                                        <Form.Control
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) =>
                                                handleMediaFileChange(m.id, e.target.files?.[0] || null)
                                            }
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Alt Text</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={m.altText}
                                                onChange={(e) =>
                                                    updateMediaField(m.id, "altText", e.target.value)
                                                }
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} className="mt-3 mt-md-0">
                                        <Form.Check
                                            type="radio"
                                            label="Primary"
                                            name="primaryMedia"
                                            checked={m.isPrimary === 1}
                                            onChange={() => setPrimaryMedia(m.id)}
                                            className="mb-2"
                                        />

                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => removeMediaRow(m.id)}
                                            disabled={mediaList.length === 1}
                                        >
                                            Remove
                                        </Button>
                                    </Col>
                                </Row>
                            );
                        })}
                    </Card.Body>
                )}
            </Card>

            {/* Ingredients */}
            <Card className="mb-3">
                <Card.Header
                    className="recipe-section-header  d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setIngredientsOpen(!ingredientsOpen)}
                >
                    <span>Ingredients *</span>
                    <div className="d-flex align-items-center">
                        <Button
                            size="sm"
                            style={{
                                border: "1px solid #ff8080ff"
                            }}
                            variant="danger"
                            className="me-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                addIngredientGroup();
                            }}
                        >
                            + Add Group
                        </Button>
                        <FontAwesomeIcon
                            icon={
                                ingredientsOpen
                                    ? "fa-solid fa-chevron-up"
                                    : "fa-solid fa-chevron-down"
                            }
                        />
                    </div>
                </Card.Header>
                {ingredientsOpen && (
                    <Card.Body>
                        {errors.ingredients && (
                            <p className="text-danger">{errors.ingredients}</p>
                        )}

                        {ingredientGroups.map((g, gIdx) => (
                            <Card className="mb-3" key={g.id}>
                                <Card.Body>
                                    <Row className="align-items-center mb-2">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Group Title (e.g. "For Rice")
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={g.title}
                                                    onChange={(e) =>
                                                        updateGroupTitle(g.id, e.target.value)
                                                    }
                                                    placeholder={`Group ${gIdx + 1}`}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col
                                            md={6}
                                            className="d-flex justify-content-end mt-2 mt-md-0 gap-2"
                                        >
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => toggleIngredientGroupCollapsed(g.id)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        g.collapsed
                                                            ? "fa-solid fa-chevron-down"
                                                            : "fa-solid fa-chevron-up"
                                                    }
                                                    className="me-1"
                                                />
                                                {g.collapsed ? "Show" : "Hide"}
                                            </Button>

                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => removeIngredientGroup(g.id)}
                                                disabled={ingredientGroups.length === 1}
                                            >
                                                <FontAwesomeIcon
                                                    icon="fa-solid fa-trash"
                                                    className="me-1"
                                                />
                                                Remove Group
                                            </Button>
                                        </Col>
                                    </Row>

                                    {!g.collapsed && (
                                        <>
                                            {g.ingredients.map((ing) => (
                                                <Row key={ing.id} className="mb-2">
                                                    <Col md={7}>
                                                        <Form.Group>
                                                            <Form.Label>Ingredient</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={ing.ingredientName}
                                                                onChange={(e) =>
                                                                    updateIngredientField(
                                                                        g.id,
                                                                        ing.id,
                                                                        "ingredientName",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Group>
                                                            <Form.Label>Quantity</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="2 cups"
                                                                value={ing.quantityText}
                                                                onChange={(e) =>
                                                                    updateIngredientField(
                                                                        g.id,
                                                                        ing.id,
                                                                        "quantityText",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col
                                                        md={1}
                                                        className="d-flex align-items-end justify-content-md-center"
                                                    >
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            style={{
                                                                margin: "0.5rem",
                                                            }}
                                                            onClick={() =>
                                                                removeIngredientRow(g.id, ing.id)
                                                            }
                                                            disabled={g.ingredients.length === 1}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon="fa-solid fa-trash"
                                                                className="me-1"
                                                            />
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ))}

                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => addIngredientRow(g.id)}
                                            >
                                                + Add Ingredient
                                            </Button>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        ))}
                    </Card.Body>
                )}
            </Card>

            {/* Mapped Products */}
            <Card className="mb-3">
                <Card.Header
                    className="recipe-section-header d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setProductsOpen(!productsOpen)}
                >
                    <span>Mapped Products (Optional)</span>
                    <div>
                        <FontAwesomeIcon
                            icon={
                                productsOpen
                                    ? "fa-solid fa-chevron-up"
                                    : "fa-solid fa-chevron-down"
                            }
                        />
                    </div>
                </Card.Header>
                {productsOpen && (
                    <Card.Body>
                        {/* Search + quantity + add row */}
                        <Row className="mb-3" style={{ position: "relative" }}>
                            <Col md={7}>
                                <Form.Group>
                                    <Form.Label>Search Product Item</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Type to search..."
                                        value={productSearchText}
                                        onChange={handleProductSearchChange}
                                    />
                                </Form.Group>

                                {productSearchResults.length > 0 && (
                                    <div
                                        style={{
                                            border: "1px solid #ccc",
                                            borderRadius: "4px",
                                            background: "#fff",
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            position: "absolute",
                                            zIndex: 999,
                                            width: "100%",
                                            marginTop: "2px",
                                        }}
                                    >
                                        {productSearchResults.map((item) => (
                                            <div
                                                key={item.id}
                                                style={{
                                                    padding: "6px 8px",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() =>
                                                    handleSelectProductSuggestion(
                                                        item
                                                    )
                                                }
                                            >
                                                {item.label}
                                                {item.code
                                                    ? ` (${item.code})`
                                                    : ""}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Default Quantity</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Example: 1 pack"
                                        value={productQtyText}
                                        onChange={(e) =>
                                            setProductQtyText(e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col
                                md={2}
                                className="d-flex align-items-end justify-content-end"
                            >
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAddMappedProduct}
                                    disabled={!selectedProductItem}
                                >
                                    + Add Product
                                </Button>
                            </Col>
                        </Row>

                        {/* Existing / added items list */}
                        {mappedProducts.length > 0 && (
                            <Card>
                                <Card.Body>
                                    <Row className="fw-bold mb-2">
                                        <Col md={6}>Product Item</Col>
                                        <Col md={3}>Quantity</Col>
                                        <Col md={3} className="text-end">
                                            Actions
                                        </Col>
                                    </Row>

                                    {mappedProducts.map((p, idx) => (
                                        <Row
                                            key={p.id}
                                            className="align-items-center mb-2"
                                        >
                                            <Col md={6}>
                                                {p.label}
                                                {p.code
                                                    ? ` (${p.code})`
                                                    : ""}
                                            </Col>
                                            <Col md={3}>
                                                <Form.Control
                                                    type="text"
                                                    value={p.quantityText}
                                                    onChange={(e) =>
                                                        handleChangeMappedQuantity(
                                                            p.id,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Col>
                                            <Col
                                                md={3}
                                                className="d-flex justify-content-end gap-2"
                                            >
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() =>
                                                        moveMappedProduct(
                                                            idx,
                                                            -1
                                                        )
                                                    }
                                                    disabled={idx === 0}
                                                >
                                                    <FontAwesomeIcon icon="fa-solid fa-arrow-up" />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() =>
                                                        moveMappedProduct(
                                                            idx,
                                                            1
                                                        )
                                                    }
                                                    disabled={
                                                        idx ===
                                                        mappedProducts.length -
                                                        1
                                                    }
                                                >
                                                    <FontAwesomeIcon icon="fa-solid fa-arrow-down" />
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleRemoveMappedProduct(
                                                            p.id
                                                        )
                                                    }
                                                >
                                                    <FontAwesomeIcon icon="fa-solid fa-trash" />
                                                </Button>
                                            </Col>
                                        </Row>
                                    ))}
                                </Card.Body>
                            </Card>
                        )}
                    </Card.Body>
                )}
            </Card>

            {/* Steps */}
            <Card className="mb-4">
                <Card.Header
                    className="recipe-section-header d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setStepsOpen(!stepsOpen)}
                >
                    <span>How to Make *</span>
                    <div className="d-flex align-items-center">
                        <Button
                            size="sm"
                            style={{
                                border: "1px solid #ff8080ff"
                            }}
                            variant="danger"
                            className="me-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                addStepSection();
                            }}
                        >
                            + Add Section
                        </Button>
                        <FontAwesomeIcon
                            icon={
                                stepsOpen
                                    ? "fa-solid fa-chevron-up"
                                    : "fa-solid fa-chevron-down"
                            }
                        />
                    </div>
                </Card.Header>
                {stepsOpen && (
                    <Card.Body>
                        {errors.steps && (
                            <p className="text-danger">{errors.steps}</p>
                        )}

                        {stepSections.map((s, sIdx) => (
                            <Card className="mb-3" key={s.id}>
                                <Card.Body>
                                    <Row className="align-items-center mb-2">
                                        <Col md={8}>
                                            <Form.Group>
                                                <Form.Label>Section Title</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={s.title}
                                                    onChange={(e) =>
                                                        updateSectionTitle(
                                                            s.id,
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder={`Section ${sIdx + 1} (e.g. "Preparation")`}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col
                                            md={4}
                                            className="d-flex justify-content-end mt-2 mt-md-0 gap-2"
                                        >
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() =>
                                                    toggleStepSectionCollapsed(
                                                        s.id
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        s.collapsed
                                                            ? "fa-solid fa-chevron-down"
                                                            : "fa-solid fa-chevron-up"
                                                    }
                                                    className="me-1"
                                                />
                                                {s.collapsed ? "Show" : "Hide"}
                                            </Button>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() =>
                                                    removeStepSection(s.id)
                                                }
                                                disabled={
                                                    stepSections.length === 1
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon="fa-solid fa-trash"
                                                    className="me-1"
                                                />
                                                Remove Section
                                            </Button>
                                        </Col>
                                    </Row>

                                    {!s.collapsed && (
                                        <>
                                            {s.steps.map((st) => (
                                                <Row key={st.id} className="mb-2">
                                                    <Col md={11}>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            value={st.description}
                                                            onChange={(e) =>
                                                                updateStepDescription(s.id, st.id, e.target.value)
                                                            }
                                                            placeholder="Instruction line"
                                                            style={{ resize: "vertical" }}
                                                        />
                                                    </Col>
                                                    <Col
                                                        md={1}
                                                        className="d-flex align-items-start justify-content-center"
                                                    >
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            style={{
                                                                margin: "0.1rem 0.4rem",
                                                            }}
                                                            onClick={() => removeStepLine(s.id, st.id)}
                                                            disabled={s.steps.length === 1}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon="fa-solid fa-trash"
                                                                className="me-1"
                                                            />
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ))}



                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() =>
                                                    addStepLine(s.id)
                                                }
                                            >
                                                + Add Step
                                            </Button>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        ))}
                    </Card.Body>
                )}
            </Card>
        </Container>
    );
};

export default RecipeForm;
